import { MonitorHeader } from "./components/MonitorHeader";
import { Box, Typography } from "@mui/material";
import {
    getCompaniesEquipment,
    getEquipmentData,
    getMonitorRequestErrors,
    getNoSyncEquipment,
    MONITOR_COLORS
} from "./config/functions";
import { useEffect, useState } from "react";
import { MonitorCard } from "./components/MonitorCard";
import { MonitorCompanyEquipment } from "./components/charts/MonitorCompanyEquipment";
import { MonitorNoSyncEquipment } from "./components/charts/MonitorNoSyncEquipment";
import { MonitorErrorsRequest } from "./components/charts/MonitorErrorsRequest";
import { MonitorLast12HoursError } from "./components/charts/MonitorLast12HoursError";
import { MonitorErrorsType } from "./components/charts/MonitorErrorsType";
import { useKeepAlive } from "../../../../hooks/keepUserSession";

const RELOAD_TIME = 30000;
const ZOOM = 0.8;

// Espaçamento fixo em px — com zoom 0.8 fica visualmente * 0.8
// então 16px vira ~13px na tela, que é suficiente
const GAP = '16px';

export function MonitorDashboard() {
    useKeepAlive();

    const [loading, setLoading] = useState(true);
    const [empresas, setEmpresas] = useState(null);
    const [equipmentData, setEquipmentData] = useState(null);
    const [noSyncEquipment, setNoSyncEquipment] = useState(null);
    const [requestErrors, setRequestErrors] = useState(null);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;

        html.style.backgroundColor = MONITOR_COLORS.primary;
        html.style.overflow = 'hidden';
        html.style.height = '100%';

        body.style.zoom = ZOOM;
        body.style.overflow = 'hidden';
        body.style.backgroundColor = MONITOR_COLORS.primary;
        body.style.margin = '0';
        body.style.padding = '0';
        body.style.height = '100%';

        return () => {
            html.style.backgroundColor = '';
            html.style.overflow = '';
            html.style.height = '';
            body.style.zoom = '';
            body.style.overflow = '';
            body.style.backgroundColor = '';
            body.style.margin = '';
            body.style.padding = '';
            body.style.height = '';
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const [
                resEquipment,
                resEmpresas,
                resNoSync,
                resRequestErrors
            ] = await Promise.all([
                getEquipmentData(),
                getCompaniesEquipment(),
                getNoSyncEquipment(),
                getMonitorRequestErrors()
            ]);

            setEquipmentData(resEquipment.data);
            setEmpresas(resEmpresas.data);
            setNoSyncEquipment(resNoSync.data);
            setRequestErrors(resRequestErrors.data);
            setLoading(false);
        };

        fetchData();
        const interval = setInterval(fetchData, RELOAD_TIME);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <Box sx={{
                position: 'fixed',
                inset: 0,
                backgroundColor: MONITOR_COLORS.primary,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
            }}>
                <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    border: `4px solid ${MONITOR_COLORS.tertiary}`,
                    borderTop: `4px solid ${MONITOR_COLORS.text.secondary}`,
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                    },
                }} />
                <Typography sx={{
                    color: MONITOR_COLORS.text.secondary,
                    fontSize: '0.9rem',
                    letterSpacing: '0.1em',
                    fontFamily: 'monospace',
                }}>
                    Carregando dashboard...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100vw',
            backgroundColor: MONITOR_COLORS.primary,
            overflow: 'hidden',
        }}>
            <MonitorHeader />

            <Box sx={{
                position: 'absolute',
                top: '11vh',
                left: 0,
                right: 0,
                bottom: 0,
                padding: GAP,
                display: 'flex',
                flexDirection: 'column',
                rowGap: GAP,
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}>
                <Box sx={{
                    flex: '0 0 calc(52% - 8px)',
                    minHeight: 0,
                    display: 'flex',
                    columnGap: GAP,
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: GAP,
                        flexShrink: 0,
                        flex: '0 0 42%',
                        minWidth: 0,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            columnGap: GAP,
                            flexShrink: 0,
                        }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <MonitorCard
                                    type="machine"
                                    total={equipmentData?.maquinas?.total}
                                    active={equipmentData?.maquinas?.ativo}
                                    offline={equipmentData?.maquinas?.offline}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <MonitorCard
                                    type="locker"
                                    total={equipmentData?.armarios?.total}
                                    active={equipmentData?.armarios?.ativo}
                                    offline={equipmentData?.armarios?.offline}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <MonitorCard
                                    type="counter"
                                    total={equipmentData?.balcoes?.total}
                                    active={equipmentData?.balcoes?.ativo}
                                    offline={equipmentData?.balcoes?.offline}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MonitorNoSyncEquipment noSyncEquipment={noSyncEquipment} />
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MonitorCompanyEquipment empresas={empresas} />
                    </Box>
                </Box>

                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    columnGap: GAP,
                }}>
                    <Box sx={{ flex: '0 0 calc(33% - 11px)', minWidth: 0, marginRight: '2vw' }}>
                        <MonitorErrorsRequest requestErrors={requestErrors} />
                    </Box>

                    <Box sx={{ flex: '0 0 calc(42% - 11px)', minWidth: 0, marginRight: '2vw' }}>
                        <MonitorLast12HoursError requestErrors={requestErrors} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MonitorErrorsType requestErrors={requestErrors} />
                    </Box>
                </Box>

            </Box>
        </Box>
    );
}